import { Bell, ChevronDown, HelpCircle, Search, Settings } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import sidebarConfig from '../data/sidebarConfig';
import HelpModal from './HelpModal';
import NotificationsSidebar from './NotificationsSidebar';

// Search result interface
interface SearchResult {
  title: string;
  subtitle?: string;
  path: string;
  category: string;
  icon?: React.ComponentType<{ className?: string }>;
}

const Header: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showNotificationsSidebar, setShowNotificationsSidebar] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpModalSection, setHelpModalSection] = useState('overview');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);

  // Create search results from sidebar config
  const allSearchResults: SearchResult[] = React.useMemo(() => {
    const results: SearchResult[] = [];
    
    sidebarConfig.forEach(category => {
      category.items.forEach(item => {
        results.push({
          title: item.label,
          subtitle: category.label,
          path: item.path,
          category: category.label,
          icon: category.icon,
        });
      });
    });

    // Add additional pages not in sidebar
    results.push(
      { title: 'Lead Detail', subtitle: 'Sales', path: '/leads', category: 'Sales' },
      { title: 'Account Leads', subtitle: 'Sales', path: '/accounts', category: 'Sales' },
      { title: 'Contact Dashboard', subtitle: 'Contacts', path: '/contacts', category: 'Contacts' },
      { title: 'Marketing Forms', subtitle: 'Contacts', path: '/contacts/forms', category: 'Contacts' }
    );

    return results;
  }, []);

  // Filter search results based on search term
  const filteredResults = React.useMemo(() => {
    if (!searchTerm.trim()) return allSearchResults.slice(0, 8); // Show first 8 results when no search
    
    const term = searchTerm.toLowerCase();
    return allSearchResults.filter(result => 
      result.title.toLowerCase().includes(term) ||
      result.category.toLowerCase().includes(term) ||
      result.subtitle?.toLowerCase().includes(term)
    ).slice(0, 8);
  }, [searchTerm, allSearchResults]);

  // Handle search result click
  const handleSearchResultClick = (result: SearchResult) => {
    navigate(result.path);
    setSearchTerm('');
    setShowSearchDropdown(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Don't close search dropdown if clicking inside it
      if (searchDropdownRef.current && searchDropdownRef.current.contains(target)) {
        return;
      }
      
      // Don't close search dropdown if clicking on search input
      if (searchInputRef.current && searchInputRef.current.contains(target)) {
        return;
      }
      
      setShowHelp(false);
      setShowProfile(false);
      setShowSearchDropdown(false);
    };

    if (showHelp || showProfile || showSearchDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showHelp, showProfile, showSearchDropdown]);

  // Handle keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <header className="bg-slate-900 border-b border-slate-700">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left Side - Logo + Search */}
          <div className="flex items-center flex-1">
            {/* HubSpot CRM Logo */}
            {/* <div className="flex items-center space-x-3">
              <HubSpot className="h-8 w-8 text-orange-500" />
              <span className="text-white font-semibold text-lg hidden sm:block">HubSpot CRM</span>
            </div> */}

            {/* Global Search Bar */}
            <div className="ml-8 flex-1 max-w-lg relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 icon-accent pointer-events-none" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search HubSpot CRM"
                  value={searchTerm}
                  onFocus={() => setShowSearchDropdown(true)}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowSearchDropdown(true);
                    console.log('🔍 Global search:', e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setShowSearchDropdown(false);
                      setSearchTerm('');
                    }
                  }}
                  className="w-full pl-16 pr-16 py-2 bg-slate-800 border border-slate-600 rounded-md text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <span className="text-xs text-gray-400 bg-slate-700 px-1.5 py-0.5 rounded border border-slate-600">⌘K</span>
                </div>
              </div>

              {/* Search Dropdown */}
              {showSearchDropdown && (
                <div 
                  ref={searchDropdownRef}
                  className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto"
                >
                  {/* Search Results */}
                  <div className="py-2">
                    {filteredResults.length === 0 ? (
                      <div className="px-4 py-6 text-center text-gray-500">
                        <p>No results found for "{searchTerm}"</p>
                      </div>
                    ) : (
                      <>
                        {!searchTerm && (
                          <div className="px-4 py-2">
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                              Recently Selected Results
                            </h3>
                          </div>
                        )}
                        {filteredResults.map((result, index) => {
                          const IconComponent = result.icon;
                          return (
                            <button
                              key={`${result.path}-${index}`}
                              onClick={() => handleSearchResultClick(result)}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 group"
                            >
                              <div className="flex-shrink-0">
                                {IconComponent ? (
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <IconComponent className="h-4 w-4 text-blue-600" />
                                  </div>
                                ) : (
                                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                    <Search className="h-4 w-4 text-gray-500" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                  <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                                    {result.title}
                                  </p>
                                </div>
                                {result.subtitle && (
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {result.subtitle} • {result.category}
                                  </p>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Icons + Profile */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🔔 Notifications clicked');
                  setShowNotificationsSidebar(true);
                }}
                className="relative p-2 text-gray-300 hover:text-white transition-colors"
              >
                <Bell className="h-5 w-5" />
                {/* Teal notification dot */}
                <span className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full"></span>
              </button>
            </div>

            {/* Help */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('❓ Help clicked');
                  setShowHelp(!showHelp);
                }}
                className="p-2 text-gray-300 hover:text-white transition-colors"
              >
                <HelpCircle className="h-5 w-5" />
              </button>
              
              {showHelp && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-50">
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Help & Support</h3>
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          setHelpModalSection('documentation');
                          setShowHelpModal(true);
                          setShowHelp(false);
                        }}
                        className="w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded"
                      >
                        Documentation
                      </button>
                      <button
                        onClick={() => {
                          setHelpModalSection('contact');
                          setShowHelpModal(true);
                          setShowHelp(false);
                        }}
                        className="w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded"
                      >
                        Contact Support
                      </button>
                      <button
                        onClick={() => {
                          setHelpModalSection('tutorials');
                          setShowHelpModal(true);
                          setShowHelp(false);
                        }}
                        className="w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded"
                      >
                        Video Tutorials
                      </button>
                      <button
                        onClick={() => {
                          setHelpModalSection('bug-report');
                          setShowHelpModal(true);
                          setShowHelp(false);
                        }}
                        className="w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded"
                      >
                        Report a Bug
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Settings */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('⚙️ Settings clicked - navigating to settings page');
                  navigate('/settings');
                }}
                className="p-2 text-gray-300 hover:text-white transition-colors"
              >
                <Settings className="h-5 w-5" />
              </button>
            </div>

            {/* Company Dropdown */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🏢 Company clicked');
                  setShowProfile(!showProfile);
                }}
                className="flex items-center space-x-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-md transition-colors border border-slate-600"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                    <span className="text-slate-900 font-bold text-sm">C</span>
                  </div>
                  <span className="text-white text-sm font-medium">Coral AI</span>
                </div>
                <ChevronDown className="h-4 w-4 text-gray-300" />
              </button>
              
              {showProfile && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-50">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-lg">C</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Coral AI</p>
                        <p className="text-sm text-gray-500">Enterprise Plan</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-2">
                    <button
                      onClick={() => {
                        console.log('🚪 Signing out user');
                        if (window.confirm('Are you sure you want to sign out?')) {
                          console.log('User confirmed sign out');
                          // TODO: Implement actual sign out logic
                          alert('Sign out functionality will be implemented with authentication.');
                        }
                        setShowProfile(false);
                      }}
                      className="w-full text-left p-2 text-sm text-red-600 hover:bg-red-50 rounded"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notifications Sidebar */}
      <NotificationsSidebar
        isOpen={showNotificationsSidebar}
        onClose={() => setShowNotificationsSidebar(false)}
      />

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        initialSection={helpModalSection}
      />

    </header>
  );
};

export default Header;
